import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { featureTestimonial } from "../../../context/Testimonials";

export default function TestimonialFeatureDialog({ open, onClose, testimonial, onSuccess }) {
  const [isTopStory, setIsTopStory] = useState(testimonial?.isTopStory ?? false);
  const [featuredOrder, setFeaturedOrder] = useState(testimonial?.featuredOrder ?? "");
  const [autoplay, setAutoplay] = useState(testimonial?.autoplayMutedPreviewEnabled ?? true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sync when testimonial prop changes
  React.useEffect(() => {
    if (testimonial) {
      setIsTopStory(testimonial.isTopStory ?? false);
      setFeaturedOrder(testimonial.featuredOrder ?? "");
      setAutoplay(testimonial.autoplayMutedPreviewEnabled ?? true);
      setError(null);
    }
  }, [testimonial]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);
      const body = {
        isTopStory,
        featuredOrder: featuredOrder !== "" ? Number(featuredOrder) : null,
        autoplayMutedPreviewEnabled: autoplay,
      };
      const result = await featureTestimonial(testimonial._id, body);
      onSuccess(result);
      handleClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!testimonial) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="h6" fontWeight={700} p={0} m={0}>
          Feature Settings
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: "background.default" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }} p={0} m={0}>
          <strong>"{testimonial.title}"</strong>
        </Typography>

        <FormControlLabel
          control={
            <Switch
              checked={isTopStory}
              onChange={(e) => setIsTopStory(e.target.checked)}
              color="warning"
            />
          }
          label="Top Story"
          sx={{ mb: 1, display: "block" }}
        />

        <TextField
          label="Featured Order"
          type="number"
          size="small"
          fullWidth
          value={featuredOrder}
          onChange={(e) => setFeaturedOrder(e.target.value)}
          inputProps={{ min: 1, p: 0, m: 0 }}
          helperText='Leave blank to remove from featured list'
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={autoplay}
              onChange={(e) => setAutoplay(e.target.checked)}
              color="info"
            />
          }
          label="Autoplay Muted Preview"
          sx={{ display: "block" }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading} variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? "Saving…" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
